/**
 * Shared date/time helpers — one place for "now" and for the two date traps that
 * recurred across this codebase.
 *
 * 1. D1 text-date comparisons. Columns are written as ISO 8601
 *    (`new Date().toISOString()` → `2026-08-19T15:00:00.123Z`) while SQL literals
 *    use `datetime('now')` (space-separated → `2026-08-19 15:00:00`). A bare
 *    string comparison is byte-wise, and 'T' (0x54) sorts after ' ' (0x20), so a
 *    same-UTC-day ISO value never compares below a same-day threshold — the
 *    comparison only flips once the calendar date rolls. Wrap the ISO column in
 *    SQLite's `datetime(col)` (which parses both forms) before comparing to
 *    `datetime('now', …)`. See `d1DateExpr`.
 *
 * 2. Business-day math. Cloudflare Workers run on UTC, so reading the day from a
 *    Date's LOCAL getters gives UTC on the server and the visitor's zone in the
 *    browser — they disagree every evening. Anchor day-of logic to a fixed
 *    business timezone with `businessDayYmd` (default America/Denver, matching
 *    the welder pricing helper).
 */

/** The oilfield business timezone (Mountain — New Mexico). */
export const BUSINESS_TZ = 'America/Denver';

/** Canonical "now" for writes: ISO-8601 UTC. */
export function nowIso(): string {
  return new Date().toISOString();
}

/**
 * A SQLite expression that normalizes an ISO-8601 text column so it compares
 * correctly against `datetime('now', …)`. `column` is a trusted identifier — a
 * literal column name in our own queries, never user input.
 */
export function d1DateExpr(column: string): string {
  return `datetime(${column})`;
}

/**
 * `YYYY-MM-DD` for `date` in the given business timezone (default America/Denver),
 * so the "which calendar day is it" answer is the same on a UTC Worker and in the
 * customer's browser. `en-CA` formats as `YYYY-MM-DD`.
 */
export function businessDayYmd(date: Date = new Date(), tz: string = BUSINESS_TZ): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** D1 datetime() values are UTC; keep explicit ISO offsets intact. */
export function databaseDate(value: string): Date {
  return new Date(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
    ? `${value.replace(' ', 'T')}Z` : value);
}
